import { Drawer, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useMemo, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { isNullish } from 'remeda';
import AddressBlock from 'shared/components/AddressBlock';
import Amount from 'shared/components/Amount';
import AYSModal from 'shared/components/AYSModal';
import BankAccount from 'shared/components/BankAccount';
import GradientBorder from 'shared/components/GradientBorder';
import HTML from 'shared/components/HTML/HTML';
import { IconName } from 'shared/components/Icon';
import InvoiceFooter from 'shared/components/InvoiceFooter';
import TvaNumber from 'shared/components/TvaNumber';
import EncodePaymentForm from 'shared/forms/EncodePaymentForm';
import SendInvoiceForm from 'shared/forms/SendInvoiceForm';
import { Button, ButtonColor } from 'shared/io';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { isDraft, isPaid } from 'shared/utils/invoice-status';
import { formatCommunication } from 'shared/utils/mask.utils';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, showDialog } from 'store/view/view.actions';
import type { StoreState } from 'types/storeTypes';
import LinkDocument from '../LinkDocument';
import styleIdentifiers from './detail.module.scss';
import TableDetails from './TableDetails';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  isCreditNote?: boolean;
  imported?: boolean;
};

// NB: This is essentially the overview of a draft invoice before validation

export default function DraftInvoice({ isCreditNote, imported }: Props) {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const invoice = useSelector((state: StoreState) => state.invoice.current?.data);
  const creditNote = useSelector((state: StoreState) => state.creditNote.current?.data);
  const { id } = useParams<{ id: string }>();
  const [pdf, setPdf] = useState<File>();
  const history = useHistory();

  const [
    isEncodePaymentModalOpen,
    { open: openEncodePaymentModal, close: closeEncodePaymentModal },
  ] = useDisclosure(false);

  const [isSendDrawerOpen, { open: openSendDrawer, close: closeSendDrawer }] = useDisclosure(false);

  //! This type is incorrect, sometimes the date fields are null which is why we safe navigate them below
  const data = isCreditNote ? creditNote : invoice;
  const formatter = useLocalizedCurrencyFormatter(false, data?.currency);

  useEffect(() => {
    if (isCreditNote) creditNoteActions.detail({ id: Number(id) });
    else invoiceActions.detail({ id: Number(id) });
  }, [isCreditNote, id]);

  useEffect(() => {
    if (data?.imported) {
      invoiceActions.document({
        id: Number(id),
        document_type: 'invoice_pdf',
        callback: (doc: File) => {
          setPdf(doc);
        },
      });
    }
  }, [data?.imported, id]);

  const nextRef = useMemo(() => {
    const billingLog = data?.billing_log_attributes;
    return billingLog != null
      ? `${billingLog.prefix}-${billingLog.counter + 1} (${t('INVOICE.DRAFT')})`
      : '';
  }, [data, t]);

  // Only for invoice
  const submitPayment = (currency: unknown) => (values: Record<string, unknown>) => {
    if (!data) return;

    invoiceActions.actionsAddPayment({
      id: data.id,
      data: {
        currency,
        ...values,
      },
      callback: () => {
        invoiceActions.detail({
          id: data.id,
          noReset: true,
          noLoading: true,
        });
        closeEncodePaymentModal();
      },
    });
  };

  // Only for invoice
  const addPayment = () => {
    if (isNullish(data)) return;
    openEncodePaymentModal();
  };

  const linkDocument = () => {
    if (isNullish(data)) return;
    showDialog({
      id: DialogShowId.CUSTOM,
      title: t(i18nKeys.INVOICE.JOIN_FILE),
      children: <LinkDocument invoice={data} />,
    });
  };

  const deleteItem = () => {
    if (!data) return;

    closeSendDrawer();

    showDialog({
      id: DialogShowId.CONFIRM,
      title: t(i18nKeys.ATTENTION),
      children: (
        <AYSModal
          text={t(isCreditNote ? 'INVOICE.AYS_TO_DELETE_CREDIT_NOTE' : 'AYS.INVOICE_ACTION_DELETE')}
          onConfirm={() => {
            (isCreditNote ? creditNoteActions : invoiceActions).delete({
              id: data.id,
              callback: () => {
                history.push(isCreditNote ? '/credit-notes' : '/invoices');
              },
            });
          }}
        />
      ),
    });
  };

  const sendInvoice = (values: unknown) => {
    if (!data) return;

    if (isCreditNote) {
      creditNoteActions.send({
        id: data.id,
        data: values,
        callback: () => {
          closeSendDrawer();
          history.push('/credit-notes');
        },
      });
    } else {
      invoiceActions.send({
        id: data.id,
        data: values,
        callback: closeSendDrawer,
      });
    }
  };

  const validate = () => {
    if (!data) return;

    openSendDrawer();
  };

  const handleDownload = () => {
    invoiceActions.document({
      id: data?.id,
      type: 'invoice_pdf',
    });
  };

  const customer = data?.debtor_attributes;
  const shouldHideVat = customer?.intracom || customer?.co_contractor;
  const remaining = (() => {
    if (data == null) return 0;
    if (data.remaining_balance !== 0) return data.remaining_balance;
    return data.total_tvac - data.already_paid_amount;
  })();

  return (
    <div className={styles('Detail')}>
      {data && (
        <>
          <div className={styles('head')}>
            <div className={styles('left')}>
              <Button noMargin onClick={handleDownload}>
                {t(i18nKeys.INVOICE.DOWNLOAD_PDF)}
              </Button>
            </div>
            {!imported && (
              <div className={styles('actions')}>
                {data.actions.includes('delete') && (
                  <Button
                    noMargin
                    label={t(i18nKeys.DELETE)}
                    color={ButtonColor.RED}
                    onClick={deleteItem}
                  />
                )}
                {data.actions.includes('edit') && (
                  <Button
                    onClick={() =>
                      history.push(`/${isCreditNote ? 'credit-notes' : 'invoices'}/${data.id}/edit`)
                    }
                    noMargin
                    label={t(i18nKeys.EDIT)}
                    color={ButtonColor.GREY}
                  />
                )}
                {data.actions.includes('add_payment') && (
                  <Button
                    noMargin
                    label={t(i18nKeys.INVOICE.ADD_PAYMENT)}
                    color={ButtonColor.GREY}
                    onClick={addPayment}
                  />
                )}
                {!isPaid(data) && !isCreditNote && (
                  <Button noMargin color={ButtonColor.GREY} onClick={linkDocument}>
                    {t(i18nKeys.INVOICE.JOIN_FILE)}
                    <div className={styles('text-circle', 'absolute', 'border', 'grey')}>
                      {data.additional_files_attributes.length}
                    </div>
                  </Button>
                )}
                {data.actions.includes('send_to_debtor') && (
                  <Button
                    noMargin
                    label={t(
                      isCreditNote
                        ? i18nKeys.INVOICING.SEND_CREDIT_NOTE
                        : i18nKeys.INVOICING.VALIDATE_INVOICE,
                    )}
                    color={ButtonColor.MAIN}
                    onClick={validate}
                    iconRight={IconName.MINIMAL_RIGHT}
                  />
                )}
              </div>
            )}
          </div>
          {data.imported ? (
            <div className={styles('pdf')}>
              <Document file={pdf}>
                <Page renderAnnotationLayer={false} renderTextLayer={false} pageNumber={1} />
              </Document>
            </div>
          ) : (
            <div className={styles('invoice')}>
              <GradientBorder />
              <div className={styles('content')}>
                {company.invoice_customization.logo_url && (
                  <div className={styles('head')}>
                    <img
                      className={styles('logo')}
                      src={company.invoice_customization.logo_url}
                      alt=""
                    />
                  </div>
                )}
                <div className={styles('infos')}>
                  {company && (
                    <div className={styles('company-detail')}>
                      <div className={styles('name')}>{company.name}</div>
                      <AddressBlock address={company.address_attributes} />
                      <br />
                      {company.vat_number && (
                        <div className={styles('vat-number')}>
                          {t(i18nKeys.VAT)}: <TvaNumber value={company.vat_number} />
                        </div>
                      )}
                    </div>
                  )}
                  {customer && (
                    <div className={styles('main-detail', 'right')}>
                      <div className={styles('name')}>{customer.full_name}</div>
                      <AddressBlock address={customer.address_attributes} />
                      <br />
                      {customer.vat_number && (
                        <div className={styles('vat-number')}>
                          {t(i18nKeys.VAT)} : <TvaNumber value={customer.vat_number} />
                        </div>
                      )}
                      {customer.co_contractor && (
                        <div className={styles('customer-type')}>
                          {t(i18nKeys.INVOICE.REVERSE_CHARGE_COCONTRACTOR)}
                        </div>
                      )}
                      {customer.intracom && (
                        <div className={styles('customer-type')}>
                          {t(i18nKeys.INVOICE.INTRACOMMUNITY)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles('invoice-infos')}>
                  <div>
                    <div className={styles('invoice-number')}>
                      {(!isDraft(data) || nextRef) &&
                        `${t(i18nKeys.NR)} : ${isDraft(data) ? nextRef : data.reference}`}
                    </div>
                    {data.order_reference && (
                      <div className={styles('order-reference')}>
                        <span className={styles('label')}>
                          {t(i18nKeys.INVOICING.CREATE.ORDER_REFERENCE)} :
                        </span>
                        <span>{data.order_reference}</span>
                      </div>
                    )}
                    {remaining > 0 && (
                      <div className={styles('recap')}>
                        <span>
                          <HTML
                            className={styles('inline')}
                            html={t(i18nKeys.INVOICING.INVOICE_TEXT.GIVE_THE_AMOUNT, {
                              text: formatter.format(remaining),
                            })}
                          />
                          <b>
                            {company.bank_accounts && (
                              <BankAccount value={company.bank_accounts[0]?.iban} />
                            )}
                          </b>
                        </span>
                        {data.structured_communication && (
                          <HTML
                            className={styles('inline')}
                            html={t(i18nKeys.INVOICING.INVOICE_TEXT.WITH_COMMUNICATION, {
                              text: formatCommunication(data.structured_communication),
                            })}
                          />
                        )}
                        <HTML
                          className={styles('inline')}
                          html={t(i18nKeys.INVOICING.INVOICE_TEXT.BEFORE_DEADLINE, {
                            date: dayjs(data?.due_date?.toISOString()).format('L'),
                          })}
                        />
                      </div>
                    )}
                  </div>
                  <div className={styles('right')}>
                    {data.structured_communication && (
                      <div className={styles('info')}>
                        <span className={styles('label')}>{t(i18nKeys.COMMUNICATION)} :</span>
                        <span>{formatCommunication(data.structured_communication)}</span>
                      </div>
                    )}
                    <div className={styles('info')}>
                      <span className={styles('label')}>{t(i18nKeys.INVOICING.INVOICE_DATE)}:</span>
                      {dayjs(data?.issue_date?.toISOString()).format('L')}
                    </div>
                    <div className={styles('info')}>
                      <span className={styles('label')}>{t(i18nKeys.DUE_DATE)} :</span>
                      {dayjs(data?.due_date?.toISOString()).format('L')}
                    </div>
                  </div>
                </div>
                <div className={styles('products-detail')}>
                  <TableDetails
                    noTva={shouldHideVat}
                    suffix={data.currency}
                    items={data.product_lines_attributes}
                  />
                </div>
                <div className={styles('summary')}>
                  <div className={styles('notes')}>
                    <span className={styles('main')}>{t(i18nKeys.NOTES)}</span>
                    {data.comment && <span className={styles('secondary')}>{data.comment}</span>}
                  </div>
                  <div className={styles('totals')}>
                    <div>
                      <div className={styles('main')}>
                        {t(i18nKeys.TOTAL_NOT_VAT_PRICE)} :
                        <Amount value={data.total_htva} suffix={data.currency} />
                      </div>
                      <div className={styles('secondary')}>
                        {t(i18nKeys.VAT)} :
                        <Amount value={data.total_tvac - data.total_htva} suffix={data.currency} />
                      </div>
                    </div>
                    <div className={styles('middle')}>
                      <div className={styles('main')}>
                        {t(i18nKeys.TOTAL_VAT_PRICE)} :
                        <Amount value={data.total_tvac} suffix={data.currency} />
                      </div>
                      <div className={styles('secondary')}>
                        {t(i18nKeys.INVOICING.ALREADY_PAID)} :
                        <Amount value={data.already_paid_amount} suffix={data.currency} />
                      </div>
                    </div>

                    <div className={styles('total')}>
                      <div className={styles('main')}>
                        {t(i18nKeys.INVOICING.TOTAL_OUTSTANDING_PAYMENTS)} :
                        <Amount value={remaining} suffix={data.currency} />
                      </div>
                      <div className={styles('secondary')}>
                        {t(i18nKeys.DUE_DATE)} : {dayjs(data?.due_date?.toISOString()).format('L')}
                      </div>
                    </div>
                  </div>
                </div>
                <InvoiceFooter />
              </div>
              <GradientBorder bottom />
            </div>
          )}
          <Drawer position="right" opened={isSendDrawerOpen} onClose={closeSendDrawer}>
            <SendInvoiceForm onSubmit={sendInvoice} data={data} credit={isCreditNote} />
          </Drawer>
          <Modal
            size={'lg'}
            opened={isEncodePaymentModalOpen}
            onClose={closeEncodePaymentModal}
            title={t(i18nKeys.SHARED.ENCODE_PAYMENT.TITLE)}
          >
            <EncodePaymentForm
              onClose={closeEncodePaymentModal}
              onSubmit={submitPayment(data.currency)}
              currency={data.currency}
              totalAmount={data.total_tvac}
            />
          </Modal>
        </>
      )}
    </div>
  );
}
